import { Timestamp } from '@firebase/firestore-types';
import { Languages } from '../languages';
import { Keyword } from './keyword';

/** Keep original PDFs below the 50 MB limit for OpenAI file inputs. */
export const MAX_EXTERNAL_ARTICLE_PDF_BYTES = 49_000_000;

export enum ExternalArticleType {
    MELANGES = 'melanges',
    JOURNAL = 'journal',
}

export interface ExternalArticlePublication {
    title: string;
    publishers: string[];
    year: number;
}

/** PDFs live at `${StorageCollectionName.EXTERNAL_ARTICLES_FULL_TEXT}/${articleId}.pdf`. */
interface ExternalArticleCore<D extends Timestamp | Date> {
    type: ExternalArticleType;
    title: string;
    /** Short French summary for UI display. */
    summaryShortFr?: string;
    /** Short English summary for UI display. */
    summaryShortEn?: string;
    /** Detailed summary in the article's language, identified by `language`. */
    summaryLong?: string;
    /** Names in source order. Do not split names from the final workbook on spaces. */
    authors: string[];
    language: Languages;
    /** French keyword book and optional node path. An empty path selects the whole book. */
    keyword: Keyword;
    /** English counterpart, when available. Missing attachments fall back to `keyword`. */
    keywordEn?: Keyword;
    publication: ExternalArticlePublication;
    /** Source fingerprint for bulk-import reruns and collision checks. Not used for manual BO imports. */
    importKey?: string;
    isPublished: boolean;
    createdAt: D;
    updatedAt: D;
}

export type ExternalArticle = ExternalArticleCore<Timestamp>;
export type ExternalArticleRaw = ExternalArticleCore<Date>;

export type ExternalArticleWithId = ExternalArticle & { id: string };

export type ExternalArticleMetadata = Pick<
    ExternalArticleWithId,
    | 'id'
    | 'type'
    | 'title'
    | 'authors'
    | 'language'
    | 'keyword'
    | 'keywordEn'
    | 'publication'
    | 'isPublished'
>;

export interface LibrarySubject {
    id: string;
    title: string;
    years: number[];
    articleCount: number;
    facets: {
        year: number;
        language: Languages;
        type: ExternalArticleType;
        count: number;
    }[];
}

export type ExternalArticlePreview = ExternalArticleMetadata & {
    excerpt: string;
};
